import { useState } from 'react';
import {
  Card, CardHeader, Button, Modal, Field, Input, Select,
  EmptyState, CopyField, ErrorNotice,
} from '../../components/ui/index.js';
import { PaymentsTable, PAYMENT_STATUS_OPTIONS } from '../../components/PaymentsTable.jsx';
import { useResource, useMutation, newIdempotencyKey } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { toMinorUnits, formatMoney } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { CreditCard } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY', 'SGD', 'AED', 'NZD'];

/**
 * §13: "Latest payments; create a payment from the UI and get a shareable
 * checkout link." A merchant should be able to take their first payment without
 * writing any code at all.
 */
function CreatePaymentDialog({ open, onClose, onCreated, defaultCurrency }) {
  const toast = useToast();
  const [form, setForm] = useState({ amount: '', currency: defaultCurrency, description: '', orderId: '', email: '' });
  const [created, setCreated] = useState(null);
  const [amountError, setAmountError] = useState(null);
  // One key per dialog, so a double-clicked submit cannot create two payments.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey('payment'));

  const reset = () => {
    setForm({ amount: '', currency: defaultCurrency, description: '', orderId: '', email: '' });
    setCreated(null);
    setAmountError(null);
    setIdempotencyKey(newIdempotencyKey('payment'));
  };

  const { mutate, loading, error } = useMutation(async () => {
    // TS-9: converted to minor units here, and rejected if the merchant typed
    // more precision than the currency has.
    const amount = toMinorUnits(form.amount, form.currency);
    if (amount == null || amount <= 0) {
      setAmountError(`Enter an amount like 10.50 — ${form.currency} amounts cannot be more precise than that.`);
      return;
    }
    setAmountError(null);

    const payment = await http.post(
      '/dashboard/payments',
      {
        amount,
        currency: form.currency,
        ...(form.description ? { description: form.description } : {}),
        ...(form.orderId ? { orderId: form.orderId } : {}),
        ...(form.email ? { customer: { email: form.email } } : {}),
      },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );

    setCreated(payment);
    toast.success('Payment created', { description: 'Send the checkout link to your customer.' });
    onCreated?.();
  });

  const fieldErrors = error?.fieldErrors ?? {};
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (created) {
    return (
      <Modal
        open={open}
        onClose={() => { reset(); onClose(); }}
        title="Payment created"
        blurb={`${formatMoney(created.amount, created.currency)} — share this link with your customer.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => { reset(); }}>Create another</Button>
            <Button onClick={() => { reset(); onClose(); }}>Done</Button>
          </>
        }
      >
        <CopyField
          label="Checkout link"
          value={created.checkoutUrl}
          monospace={false}
          hint="It expires in 60 minutes and allows up to 5 payment attempts."
        />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a payment"
      blurb="You'll get a branded checkout link to send to your customer."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={() => mutate().catch(() => {})} loading={loading}>Create payment</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {error && !Object.keys(fieldErrors).length && <ErrorNotice error={error} title="Could not create the payment" />}

        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <Field label="Amount" error={amountError ?? fieldErrors.amount} required>
            {(props) => (
              <Input
                {...props}
                // FE-15: a numeric keypad on a phone.
                inputMode="decimal"
                placeholder="10.50"
                value={form.amount}
                invalid={Boolean(amountError ?? fieldErrors.amount)}
                onChange={set('amount')}
                autoFocus
              />
            )}
          </Field>
          <Field label="Currency">
            {(props) => (
              <Select {...props} value={form.currency} onChange={set('currency')}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Description" hint="Shown to the customer on the checkout page." error={fieldErrors.description}>
          {(props) => <Input {...props} value={form.description} onChange={set('description')} placeholder="Two bags of Ethiopian" />}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Order ID" hint="Your own reference." error={fieldErrors.orderId}>
            {(props) => <Input {...props} value={form.orderId} onChange={set('orderId')} placeholder="order-4821" />}
          </Field>
          <Field label="Customer email" error={fieldErrors.email}>
            {(props) => <Input {...props} type="email" autoComplete="off" value={form.email} onChange={set('email')} placeholder="customer@example.com" />}
          </Field>
        </div>
      </form>
    </Modal>
  );
}

export function PaymentsPage() {
  const { merchant } = useAuth();
  const [skip, setSkip] = useState(0);
  const [status, setStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const query = useResource('/dashboard/payments', {
    params: { limit: 25, skip, ...(status ? { status } : {}) },
  });

  return (
    <>
      <PageHero
        icon={CreditCard}
        chip="Payments"
        title="Payments"
        blurb="Every payment on your account, newest first."
        actions={<Button onClick={() => setDialogOpen(true)}>Create payment</Button>}
      />

      <Card>
        <CardHeader
          title={status ? 'Payments' : 'All payments'}
          blurb={query.data ? `${query.data.total} ${status ? 'matching' : 'total'}` : undefined}
          actions={
            <Field>
              {(props) => (
                <Select {...props} aria-label="Filter by status" value={status}
                  onChange={(e) => { setStatus(e.target.value); setSkip(0); }}>
                  {PAYMENT_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </Select>
              )}
            </Field>
          }
        />
        <PaymentsTable
          query={query}
          onPageChange={setSkip}
          emptyState={
            status ? (
              <EmptyState
                title="No payments with that status"
                blurb="Pick another status, or show them all."
                action={<Button variant="secondary" onClick={() => { setStatus(''); setSkip(0); }}>Show all payments</Button>}
              />
            ) : (
              // DB-4: explains what will appear, and offers the action that creates it.
              <EmptyState
                title="No payments yet"
                blurb="Create one here to get a shareable checkout link, or call POST /v1/payments from your server."
                action={<Button onClick={() => setDialogOpen(true)}>Create your first payment</Button>}
              />
            )
          }
        />
      </Card>

      <CreatePaymentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={query.refetch}
        defaultCurrency={merchant?.defaultCurrency ?? 'USD'}
      />
    </>
  );
}

export default PaymentsPage;
